import { mdiProgressClock } from '@mdi/js';
import React from 'react';
import { formatDate } from '../../../services';
import styled from '@emotion/styled';
import { Notice } from '../../base/notice';

const SpacedNotice = styled(Notice)`
    margin-bottom: 10px;
`;

export interface IClaimUpdatingNoticeProps {
    /** When the newer claim data arrived (`IPendingHl7Snapshot.receivedAt`). */
    receivedAt: string;
    /**
     * Server validation messages, when the newer data failed validation. The page still shows that data; the
     * messages say why the professional record has not caught up. Leave empty where the page has its own banner.
     */
    validationErrors?: string[];
    className?: string;
}

/**
 * Explains why the claim overview shows the latest received claim data rather than the professional record: a newer
 * HL7 message arrived and has not finished processing. Stays for as long as that is true — the page reverts to the
 * professional record on the next load after it catches up.
 */
export const ClaimUpdatingNotice: React.FC<IClaimUpdatingNoticeProps> = ({
    receivedAt,
    validationErrors,
    className,
}) => {
    const received = formatDate(receivedAt, 'MMM d, yyyy h:mm a');

    if (validationErrors?.length) {
        return (
            <SpacedNotice tone="attention" className={className}>
                <div>
                    Claim data received {received} has validation errors and has not been applied yet. Patient, payer
                    and guarantor details below show that data.
                </div>
                <ul>
                    {validationErrors.map((message, index) => (
                        <li key={index}>{message}</li>
                    ))}
                </ul>
            </SpacedNotice>
        );
    }

    return (
        <SpacedNotice tone="attention" icon={mdiProgressClock} className={className}>
            This claim is being updated. Patient, payer and guarantor details below show the claim data received{' '}
            {received}.
        </SpacedNotice>
    );
};
